import React, { createContext, useContext } from 'react';
import { usePersistedSessionState } from '@edreamsodigeo/frontend-core';
import { useFeeType } from 'clientState/providers/FeeTypeProvider';
import { useParsedSearchResponseQuery } from './ParsedSearchResponseProvider';
import { findSelectedFee } from 'clientState/priceHelper';
import { isValidDeal } from 'clientState/dealHelper';
import {
  getFlexibleDatesDealTrend,
  FlexibleDatesRepricingTrendType,
} from 'clientState/flexibleDatesDealHelper';

import { priceComparator } from '@components/Sorting/Provider';
import { useUrlParams } from 'clientState/hooks/useUrlParams';
import { transformSuccessQuery } from './transformSuccessQuery';

const ItineraryListPricesBuilder = ({ dealPrice, flexibleDatesPrice } = {}) => {
  let cheapest = {
    price: {
      amount: null,
    },
  };
  const itinerariesWithPrice = [];

  return {
    add(itinerary) {
      itinerariesWithPrice.push(itinerary);
      if (
        cheapest.price.amount === null ||
        priceComparator(itinerary, cheapest) < 0
      ) {
        cheapest = itinerary;
      }
    },
    build() {
      let soldoutFlexibleDates = false;
      const trend = getFlexibleDatesDealTrend({
        itinerary: cheapest,
        flexibleDatesPrice,
      });

      setDeal({
        itinerary: cheapest,
        dealPrice,
      });

      if (
        trend === FlexibleDatesRepricingTrendType.up ||
        trend === FlexibleDatesRepricingTrendType.down
      ) {
        setFlexibleDatesDeal({
          itinerary: cheapest,
          flexibleDatesPrice,
          trend,
        });
      } else if (trend === FlexibleDatesRepricingTrendType.highest) {
        soldoutFlexibleDates = true;
      }

      return {
        cheapest,
        itinerariesWithPrice,
        soldoutFlexibleDates,
      };
    },
  };
};

const ItinerariesWithPriceContext = createContext();

export const useItinerariesWithPriceResponseQuery = () =>
  useContext(ItinerariesWithPriceContext);

const getTrend = ({ itinerary, dealPrice }) => {
  const { price } = itinerary;

  if (dealPrice === price.amount) return 'equal';
  return dealPrice < price.amount ? 'up' : 'down';
};

const setDeal = ({ itinerary, dealPrice }) => {
  const isValid = isValidDeal({ itinerary, dealPrice });
  if (!isValid) return;

  itinerary.dealInfo = {
    trend: getTrend({ itinerary, dealPrice }),
  };
};

const setFlexibleDatesDeal = ({ itinerary, flexibleDatesPrice, trend }) => {
  if (
    trend === FlexibleDatesRepricingTrendType.up ||
    trend === FlexibleDatesRepricingTrendType.down
  ) {
    itinerary.flexibleDatesDealInfo = {
      trend: getFlexibleDatesDealTrend({ itinerary, flexibleDatesPrice }),
    };
  }
};

export const addItineraryPrices = (
  { data },
  { feeType, dealPrice, flexibleDatesPrice }
) => {
  const { itineraries, defaultFeeType } = data;
  const selectedFeeType = feeType || defaultFeeType;
  const itineraryListPricesBuilder = ItineraryListPricesBuilder({
    dealPrice,
    flexibleDatesPrice,
  });
  itineraries.forEach((itinerary) => {
    const fee = findSelectedFee(itinerary, selectedFeeType);
    if (fee) {
      const itineraryWithPrice = {
        ...itinerary,
        price: fee.price,
      };
      itineraryListPricesBuilder.add(itineraryWithPrice);
    }
  });

  const { cheapest, itinerariesWithPrice, soldoutFlexibleDates } =
    itineraryListPricesBuilder.build();

  return {
    ...data,
    cheapestItineraryPrice: cheapest?.price?.amount,
    itineraries: itinerariesWithPrice,
    soldoutFlexibleDates,
  };
};

const ItinerariesPricesProvider = ({ children }) => {
  const [feeType] = useFeeType();
  const queryState = useParsedSearchResponseQuery();
  const [dealPrice] = usePersistedSessionState('deal_price');
  const { fdp } = useUrlParams(window.location.href || '');

  const nextQueryState = transformSuccessQuery(queryState, addItineraryPrices, {
    feeType,
    dealPrice,
    flexibleDatesPrice: fdp,
  });

  return (
    <ItinerariesWithPriceContext.Provider value={nextQueryState}>
      {children}
    </ItinerariesWithPriceContext.Provider>
  );
};

export default ItinerariesPricesProvider;
